import type React from "react";
import { useRef, useEffect, useState } from "react";
import { useCombobox } from "downshift";
import { type InjectedIntlProps, injectIntl } from "react-intl";

import { useSearchOverlay, SortDropdown } from "@jslib/OrgSearch";
import { logger } from "@jslib/shared/utils/logger";

import { MetricIds, useMetrics } from "@/components/FeatureFlags/useMetrics";
import { useFlag } from "@/components/FeatureFlags/hooks";

import { FetchOrganizationsComponent } from "../../FetchOrganizationsComponent";
import {
  type Organization,
  type DatabaseId,
  type Country,
  type Locale,
} from "../../../lib/types/types";
import { setRef } from "../../../lib/refs/refs";
import { assertValidFunction } from "../../../lib/types/assertions";
import {
  type AsrEvent,
  CustomMetricNames,
  EventName,
  type OrgSelectedEvent,
  recordGaEvent,
} from "../../../lib/GoogleAnalytics/ga";

import { handleStateChange } from "./TypeaheadComponentHelper";
import { TypeaheadClearButtonComponent } from "./TypeaheadClearButton/TypeaheadClearButtonComponent";

export interface TypeaheadProps {
  onChange: (org: Organization) => void;
  programId: DatabaseId;
  inputHtmlId: string;
  autoFocus?: boolean;
  className?: string;
  countryCode?: Country;
  disabled?: boolean;
  minimumSearchValueLength?: number;
  placeholder?: string;
  value?: Organization;
  openOrgSearchEnabled?: boolean;
  orgSearchUrl: string;
  isRequired?: boolean;
  locale?: Locale;
  urlAddSchoolForm?: string;
  isErrored?: boolean;
  ariaDescribedBy?: string;
  onBlur?: () => void;
}

type Props = TypeaheadProps & InjectedIntlProps;

const Typeahead = ({
  onChange,
  programId,
  className,
  countryCode,
  disabled,
  minimumSearchValueLength,
  placeholder,
  value,
  inputHtmlId,
  openOrgSearchEnabled,
  orgSearchUrl,
  isRequired,
  locale,
  urlAddSchoolForm,
  isErrored,
  ariaDescribedBy,
  autoFocus = false,
  onBlur = () => {},
  intl,
}: Props) => {
  const [inputValue, setInputValue] = useState(value ? value.name : "");
  const [loadedOrgs, setLoadedOrgs] = useState([]);

  const numReworksRef = useRef(0);
  const startTimeRef = useRef(0);
  const tipsShownRef = useRef(false);
  const searchAfterTipsTrackedRef = useRef(false);

  const [showClearButton, setShowClearButton] = useState(false);
  const [disabledClearButton, setDisabledClearButton] = useState(false);
  const { recordNumericMetric, trackEvent } = useMetrics({ programId });
  const overlayEnabled = useFlag("org-search-overlay") === "overlay";
  const { showOverlay, setShowOverlay } = useSearchOverlay();

  assertValidFunction(onChange);

  const itemToString = (item: Organization) => (item ? item.name : "");

  const { isOpen, highlightedIndex, getMenuProps, getItemProps, getInputProps } = useCombobox({
    itemToString,
    id: inputHtmlId,
    items: loadedOrgs,
    inputValue,
    onStateChange: (changes) =>
      handleStateChange(changes, onChange, {
        openOrgSearchEnabled,
        _searchOverlayEnabled: showOverlay,
      }),
    selectedItem: value,
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        setInputValue("");
        setShowOverlay(false);
        onChange({
          id: 0,
          name: "",
        });
        return;
      }

      setInputValue(itemToString(selectedItem));

      const selectedRank = loadedOrgs.findIndex((o) => o.id === selectedItem.id) + 1;
      const timeUntilSelection = Date.now() - startTimeRef.current;
      try {
        const event: OrgSelectedEvent = {
          eventName: EventName.ORG_SELECTED,
          params: {
            [CustomMetricNames.QUERY_LENGTH]: inputValue.length,
            [CustomMetricNames.NUMBER_REWORKS]: numReworksRef.current,
            [CustomMetricNames.SELECTED_RESULT_RANK]: selectedRank,
            [CustomMetricNames.EVENT_DURATION]: timeUntilSelection,
            [CustomMetricNames.NUMBER_OF_RESULTS]: loadedOrgs.length,
          },
        };
        recordGaEvent(event);
      } catch (e) {
        logger.error(e);
      }

      try {
        recordNumericMetric(MetricIds.OrgSearchNumReworks, numReworksRef.current);
        recordNumericMetric(MetricIds.OrgSearchQueryLength, inputValue.length);
        recordNumericMetric(MetricIds.OrgSearchResultRank, selectedRank);
        recordNumericMetric(MetricIds.OrgSearchTimeUntilSelection, timeUntilSelection);
      } catch (e) {
        logger.error(e);
      }

      startTimeRef.current = 0;
      numReworksRef.current = 0;
      setShowOverlay(false);
      onChange(selectedItem);
    },
  });

  const onClickAddSchool = () => {
    try {
      const event: AsrEvent = {
        eventName: EventName.ASR_SELECTED,
        params: {
          [CustomMetricNames.QUERY_LENGTH]: inputValue.length,
          [CustomMetricNames.NUMBER_REWORKS]: numReworksRef.current,
          [CustomMetricNames.EVENT_DURATION]: Date.now() - startTimeRef.current,
          [CustomMetricNames.NUMBER_OF_RESULTS]: loadedOrgs.length,
        },
      };
      recordGaEvent(event);
      trackEvent(MetricIds.AsrClickThrough);
    } catch (e) {
      logger.error(e);
    }
  };

  const onTipsShown = () => {
    tipsShownRef.current = true;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value;

    if (newInputValue) {
      setDisabledClearButton(false);
    } else {
      setDisabledClearButton(true);
    }

    try {
      if (startTimeRef.current === 0) {
        startTimeRef.current = Date.now();
      }
      if (newInputValue.length < inputValue.length) {
        numReworksRef.current += 1;
      }

      // Track when user searches again after seeing tips (only once)
      if (tipsShownRef.current && !searchAfterTipsTrackedRef.current && newInputValue) {
        searchAfterTipsTrackedRef.current = true;
        recordGaEvent({
          eventName: EventName.ASR_SEARCH_AFTER_TIPS,
          params: { programId },
        });
        trackEvent(MetricIds.AsrSearchAfterTips);
      }
    } catch (e) {
      logger.error(e);
    }

    setInputValue(newInputValue);
    onChange({
      id: 0,
      name: newInputValue,
    });
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // NOTE: Dependency array omitted to account for use of resetRef(), as useEffect with dep arr
  // will only trigger on initial render leaving the refs empty, this replicates prior version
  // where ref callback would trigger with each re-render
  useEffect(() => {
    setRef("organization", inputRef.current);
  });

  const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setShowClearButton(true);
    if (!event.currentTarget.value) {
      setDisabledClearButton(true);
    } else {
      setDisabledClearButton(false);
      setShowOverlay(true);
    }
  };

  const handleOnBlur = (event: React.FocusEvent) => {
    if (event.relatedTarget?.id !== `${inputHtmlId}-clear-button`) {
      setShowClearButton(false);
      onBlur();
    }
  };

  const handleOnInteraction = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.type === "mousedown" || ("key" in event && event.key === "Enter")) {
      setInputValue("");
      onChange({
        id: undefined,
        name: "",
      });
      inputRef.current.focus();
    }
  };

  return (
    <div className="sid-organization-list">
      <div className="sid-text-input__wrapper">
        <input
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getInputProps({
            ref: inputRef,
            onChange: handleInputChange,
            onFocus: (event) => handleOnFocus(event),
            onBlur: (event) => handleOnBlur(event),
            onKeyDown: (event) => {
              // we want to start the overlay if someone starts
              // typing, not if they are tabbing past or pressing Escape.
              if (event.key !== "Tab" && event.key !== "Escape") {
                setShowOverlay(true);
              }
            },
          })}
          className={`sid-l-full-width sid-hidden-placeholder sid-text-input ${className}`}
          disabled={disabled}
          id={inputHtmlId}
          // Autofocus is situational here, it's only added when org is cleared.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          placeholder={placeholder}
          aria-required={isRequired}
          aria-controls="sid-org-list-menu"
          aria-describedby={ariaDescribedBy}
          aria-invalid={isErrored}
          // Maintain same labelledby value as previous versions even though label has "for" attribute
          aria-labelledby={`${inputHtmlId}-label`}
        />
        {showOverlay && (
          <SortDropdown
            sortByRelevanceLabel={intl.formatMessage({
              id: "orgSearchSortByRelevance",
              defaultMessage: "Sort by Relevance",
            })}
            sortByDistanceLabel={intl.formatMessage({
              id: "orgSearchSortByDistance",
              defaultMessage: "Sort by Distance",
            })}
            accessibilityLabel={intl.formatMessage({
              id: "orgSearchSortAccessibilityLabel",
              defaultMessage: "Sort search results",
            })}
            on={(event) => {
              switch (event) {
                case "openSortMenu":
                  trackEvent(MetricIds.OrgSearchOpenSortMenu);
                  break;

                case "sortDistance":
                  trackEvent(MetricIds.OrgSearchSortDistance);
                  break;

                case "sortRelevance":
                  trackEvent(MetricIds.OrgSearchSortRelevance);
                  break;
                default:
                  break;
              }
            }}
          />
        )}
      </div>

      {showClearButton && !showOverlay ? (
        <TypeaheadClearButtonComponent
          id={`${inputHtmlId}-clear-button`}
          disabled={disabledClearButton}
          onMouseDown={handleOnInteraction}
          onKeyDown={handleOnInteraction}
          handleOnBlur={handleOnBlur}
        />
      ) : null}

      <div {...getMenuProps()} className="sid-organization-list__menu-wrapper">
        {/* when using the overlay we don't want to use downshift's isOpen */}
        {(inputValue && isOpen) || (showOverlay && inputValue) ? (
          <FetchOrganizationsComponent
            countryCode={countryCode}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            itemToString={itemToString}
            minimumSearchValueLength={minimumSearchValueLength}
            onLoaded={(loadedOrganizations) => setLoadedOrgs(loadedOrganizations)}
            programId={programId}
            orgSearchUrl={orgSearchUrl}
            locale={locale}
            urlAddSchoolForm={urlAddSchoolForm}
            onClickAddSchool={onClickAddSchool}
            onTipsShown={onTipsShown}
            openOrgSearchEnabled={openOrgSearchEnabled}
            searchUrlParams={overlayEnabled ? { format: "detailed" } : undefined}
          />
        ) : null}
      </div>
    </div>
  );
};

export const TypeaheadComponent = injectIntl(Typeahead);
